import { useState, useMemo, useEffect } from 'react'
import {
  Loader2,
  HardDrive,
  Globe,
  Shield,
  LockKeyhole,
  Edit3,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { useListStorageVolumesPaginated } from '@/services/storage'
import { useMountStorage } from '@/services/server'
import { LoadingMessage } from '@/components/loading-message'
import { ErrorMessage } from '@/components/error-message'
import { GetServerInstanceData } from '@/services/inference.gen'
import { cn } from '@/lib/utils'
import { getStorageTypeById, StorageTypeId } from '@/constants/storageTypes'

type MountStorageDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  serverInstance: GetServerInstanceData
  serverId: number
  supportsPVC?: boolean
  storageRequired?: boolean
}

export const MountStorageDialog = ({
  open,
  onOpenChange,
  serverInstance,
  serverId,
  supportsPVC = true,
  storageRequired = false,
}: MountStorageDialogProps) => {
  // Get already mounted volume IDs
  const mountedVolumeIds = useMemo(() => {
    return (serverInstance.storage_volume_info || []).map(
      (info) => info.storage_volume_id
    )
  }, [serverInstance.storage_volume_info])

  const [selectedVolumeIds, setSelectedVolumeIds] = useState<number[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 5

  // Initialize selected volumes with currently mounted ones when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedVolumeIds(mountedVolumeIds)
      setCurrentPage(1)
    }
  }, [open, mountedVolumeIds])

  const mountStorage = useMountStorage()

  const {
    data: volumeData,
    isLoading,
    isError,
  } = useListStorageVolumesPaginated({
    page: currentPage,
    pageSize,
    searchTerm,
    showAllUsers: false,
  })

  const volumes = volumeData?.volumes || []
  const totalPages = volumeData?.totalPages || 0

  const handleVolumeToggle = (volumeId: number) => {
    setSelectedVolumeIds((prev) => {
      if (prev.includes(volumeId)) {
        return prev.filter((id) => id !== volumeId)
      }
      return [...prev, volumeId]
    })
  }

  const handleMount = async () => {
    // Check if storage is required and no volumes selected
    if (storageRequired && selectedVolumeIds.length === 0) {
      return
    }

    // Confirm when unmounting all volumes
    if (selectedVolumeIds.length === 0 && mountedVolumeIds.length > 0) {
      const confirmed = window.confirm(
        'Are you sure you want to unmount all storage volumes? This will remove all mounted storage from the server.'
      )
      if (!confirmed) return
    }

    await mountStorage.mutateAsync({
      instanceId: serverId,
      volumeIds: selectedVolumeIds,
    })

    // Reset and close dialog on success
    setSelectedVolumeIds([])
    onOpenChange(false)
  }

  const isSubmitDisabled =
    mountStorage.isPending ||
    !supportsPVC ||
    (storageRequired && selectedVolumeIds.length === 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg min-h-[700px]">
        <DialogHeader>
          <DialogTitle>Manage Storage Volumes</DialogTitle>
          <DialogDescription>
            Select which storage volumes should be mounted to{' '}
            {serverInstance.instance_name}. Unchecking a volume will unmount it
            from the server.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning banner if PVC not supported */}
          {!supportsPVC && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg flex items-start gap-2">
              <InfoCircledIcon className="h-4 w-4 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Storage mounting not supported</p>
                <p className="text-xs mt-1">
                  This server template does not support mounting storage
                  volumes. The template must have PVC support enabled to mount
                  storage.
                </p>
              </div>
            </div>
          )}

          {/* Search input */}
          <Input
            placeholder="Search volumes by name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(1)
            }}
            className="w-full"
            disabled={!supportsPVC}
          />

          {/* Volume list */}
          <div className="border rounded-lg">
            {isLoading ? (
              <div className="p-8">
                <LoadingMessage
                  message="Loading storage volumes..."
                  centered
                  size="sm"
                />
              </div>
            ) : isError ? (
              <div className="p-8">
                <ErrorMessage message="Failed to load storage volumes" />
              </div>
            ) : volumes.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <HardDrive className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No storage volumes available</p>
                <p className="text-xs mt-1">
                  You don't have any storage volumes yet
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 space-y-2 min-h-[400px]">
                  {volumes.map((volume) => {
                    const typeName = getStorageTypeById(
                      volume.storage_volume.type_id
                    )?.name

                    return (
                      <div
                        key={volume.storage_volume.volume_id}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer hover:bg-muted/50',
                          selectedVolumeIds.includes(
                            volume.storage_volume.volume_id
                          ) && 'bg-muted/50 border-primary'
                        )}
                        onClick={() =>
                          supportsPVC &&
                          handleVolumeToggle(volume.storage_volume.volume_id)
                        }
                      >
                        <Checkbox
                          checked={selectedVolumeIds.includes(
                            volume.storage_volume.volume_id
                          )}
                          onCheckedChange={() =>
                            supportsPVC &&
                            handleVolumeToggle(volume.storage_volume.volume_id)
                          }
                          disabled={!supportsPVC}
                          onClick={(e) => e.stopPropagation()}
                        />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">
                              {volume.storage_volume.volume_name ||
                                `Volume ${volume.storage_volume.volume_id}`}
                            </span>
                            {volume.storage_volume.size_gb && (
                              <Badge variant="secondary" className="text-xs">
                                {volume.storage_volume.size_gb} GB
                              </Badge>
                            )}

                            {typeName ? (
                              <Badge variant="secondary" className="text-xs">
                                {typeName}
                              </Badge>
                            ) : null}

                            {/* Show warning for local storage on different node */}
                            {volume.storage_volume.type_id ===
                              StorageTypeId.LOCAL &&
                            volume.storage_volume.storage_volume_kube?.[0] &&
                            serverInstance.node_name &&
                            volume.storage_volume.storage_volume_kube[0]
                              .node_name !== serverInstance.node_name &&
                            !volume.storage_volume.origin_storage_volume_id ? (
                              <Badge
                                variant="outline"
                                className="text-xs text-amber-600 border-amber-600"
                              >
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Needs Migration
                              </Badge>
                            ) : null}

                            {volume.storage_volume.origin_storage_volume_id && (
                              <Badge
                                variant="outline"
                                className="text-xs text-green-600 border-green-600"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Migrated From ID:{' '}
                                {volume.storage_volume.origin_storage_volume_id}
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center gap-3 mt-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {volume.storage_volume.is_public ? (
                                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                                  ) : (
                                    <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                                  )}
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  {volume.storage_volume.is_public
                                    ? 'Public Volume'
                                    : 'Private Volume'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {volume.storage_volume.read_only ? (
                                    <LockKeyhole className="h-3 w-3 text-muted-foreground" />
                                  ) : (
                                    <Edit3 className="h-3 w-3 text-muted-foreground" />
                                  )}
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  {volume.storage_volume.read_only
                                    ? 'Read Only'
                                    : 'Read/Write'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            {volume.storage_volume.user && (
                              <span className="text-xs text-muted-foreground">
                                by {volume.storage_volume.user.email}
                              </span>
                            )}

                            <span className="ml-auto font-mono text-[10px] text-foreground/60">
                              ID: {volume.storage_volume.volume_id}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {totalPages > 1 && (
                  <div className="border-t p-3">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() =>
                              setCurrentPage(Math.max(1, currentPage - 1))
                            }
                            className={
                              currentPage === 1
                                ? 'pointer-events-none opacity-50'
                                : 'cursor-pointer'
                            }
                          />
                        </PaginationItem>
                        {[...Array(totalPages)].map((_, i) => (
                          <PaginationItem key={i + 1}>
                            <PaginationLink
                              onClick={() => setCurrentPage(i + 1)}
                              isActive={currentPage === i + 1}
                              className="cursor-pointer"
                            >
                              {i + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() =>
                              setCurrentPage(
                                Math.min(totalPages, currentPage + 1)
                              )
                            }
                            className={
                              currentPage === totalPages
                                ? 'pointer-events-none opacity-50'
                                : 'cursor-pointer'
                            }
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-2">
            {/* Show warning if selected volumes need migration */}
            {volumes.some(
              (v) =>
                selectedVolumeIds.includes(v.storage_volume.volume_id) &&
                v.storage_volume.type_id === StorageTypeId.LOCAL &&
                v.storage_volume.storage_volume_kube?.[0]?.node_name &&
                serverInstance.node_name &&
                v.storage_volume.storage_volume_kube[0].node_name !==
                  serverInstance.node_name &&
                !v.storage_volume.origin_storage_volume_id
            ) && (
              <Alert className="border-amber-500/50 bg-amber-500/5">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-sm">
                  Selected local storage volumes on different nodes will be
                  automatically migrated when mounted.
                </AlertDescription>
              </Alert>
            )}

            <div className="text-sm text-muted-foreground">
              {storageRequired && selectedVolumeIds.length === 0 ? (
                <span className="text-destructive">
                  At least one volume must be selected (storage is required for
                  this template)
                </span>
              ) : selectedVolumeIds.length === 0 ? (
                'No volumes selected - all volumes will be unmounted'
              ) : (
                <>
                  {selectedVolumeIds.length} volume
                  {selectedVolumeIds.length > 1 ? 's' : ''} selected
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mountStorage.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleMount} disabled={isSubmitDisabled}>
            {mountStorage.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {selectedVolumeIds.length === 0 && !storageRequired
              ? 'Unmount All'
              : 'Apply Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
